/**
 * 路由定义单点：AppRoute ↔ URL path 的互转全部在这里。
 *
 * - 新页面 = AppRoute 加一个成员 + routePath/parseRoute 各加一个分支
 *   （switch 穷尽检查兜底，漏分支编译期报错）。
 * - 深层页面全部进真实 URL（修复旧版刷新丢深层页面，legacy §2.2）。
 * - conversationKey 沿用 worker/ws/messages.ts 的 `dm:<uid>` / `group:<gid>` 口径，
 *   与草稿键、消息库键一致。
 */
import { unreachable } from '~/util/invariant';

export type HomeTab = 'chats' | 'contacts' | 'me';

export type AppRoute =
  | { readonly kind: 'home'; readonly tab: HomeTab }
  | { readonly kind: 'chat'; readonly conversationKey: string }
  | { readonly kind: 'login' }
  /** 首登强制设密码（logged-in 且 needsPassword 时守卫强制停在这，6e-2） */
  | { readonly kind: 'set-password' }
  /** 虚拟列表 10k 压测 demo（任务 7c；动态 import 不进首屏，仅人工压测与 e2e 用） */
  | { readonly kind: 'dev-vlist' }
  /** 我创建的表情包（14.17b；入口=贴纸面板「管理」钮） */
  | { readonly kind: 'sticker-packs' }
  /** 搜索加人（14.19b；入口=通讯录页顶部搜索条；两端全屏路由页） */
  | { readonly kind: 'contact-search' }
  /** 申请处理页（14.20；friend=新的朋友 / group=群通知，入口=通讯录四宫格） */
  | { readonly kind: 'contact-applies'; readonly applyKind: 'friend' | 'group' }
  /** 好友详情页（14.21b；入口=通讯录好友行 + 私聊标题区；两端全屏路由页） */
  | { readonly kind: 'friend-detail'; readonly friendId: number }
  /** 分组管理页（14.22b；入口=通讯录分组 Tab 头部；两端全屏路由页） */
  | { readonly kind: 'friend-groups' }
  /** 黑名单页（14.22b-2；入口=通讯录四宫格；两端全屏路由页） */
  | { readonly kind: 'blacklist' }
  /** 系统公告页（14.24；入口=通讯录四宫格；两端全屏路由页） */
  | { readonly kind: 'announcements' }
  /** 建群页（14.23b；入口=通讯录页顶部动作区；两端全屏路由页） */
  | { readonly kind: 'create-group' }
  /** 群设置主页（14.25b；入口=群聊标题区；两端全屏路由页，深链垫群聊页） */
  | { readonly kind: 'group-settings'; readonly groupId: number }
  /** 群管理页（14.26b；owner/admin，入口=群设置页「群管理」行；深链垫群设置页） */
  | { readonly kind: 'group-manage'; readonly groupId: number }
  /** 群成员列表页（14.27b；入口=群设置页「群成员」行；深链垫群设置页） */
  | { readonly kind: 'group-members'; readonly groupId: number }
  /** 邀请好友入群页（14.27c；入口=群设置页「邀请好友」行；深链垫群设置页） */
  | { readonly kind: 'group-invite'; readonly groupId: number }
  /** 成员进出记录页（14.27c；入口=群设置页仅群主行；深链垫群设置页） */
  | { readonly kind: 'group-log'; readonly groupId: number }
  /** 群公告页（14.28；入口=群设置页「群公告」行；成员只读、管理员+可编辑） */
  | { readonly kind: 'group-notice'; readonly groupId: number }
  /** 编辑资料页（14.29b；入口=我的 Tab 资料卡；两端全屏路由页） */
  | { readonly kind: 'me-profile' }
  /** 隐私设置页（14.30；入口=我的 Tab「隐私设置」行；两端全屏路由页） */
  | { readonly kind: 'me-privacy' }
  /** 外观与通知设置页（14.31c；入口=我的 Tab「外观与通知」行；纯本地零 HTTP） */
  | { readonly kind: 'me-appearance' }
  /** 我的二维码页（14.32；入口=我的 Tab「我的二维码」行；内容本地生成） */
  | { readonly kind: 'me-qrcode' }
  /** 账号管理页（14.34b；入口=我的 Tab「多账号与设备」行；列表纯 IndexedDB 零 HTTP） */
  | { readonly kind: 'me-accounts' }
  /** 添加账号页（14.34b-2；入口=账号管理页；登录卡片复用，logged-in 守卫放行） */
  | { readonly kind: 'add-account' }
  /** 登录设备列表页（14.34b-3；入口=账号管理页「登录设备」行；只读分页不缓存） */
  | { readonly kind: 'me-devices' }
  /** 两步验证管理页（14.34b-3；入口=账号管理页「两步验证」行；状态=profile 快照） */
  | { readonly kind: 'me-2fa' }
  /** 路线设置页（14.39b；入口=我的 Tab「路线设置」行；纯本地 kv，切换才发 1 次 config） */
  | { readonly kind: 'me-route' }
  /** 关于页（14.35a；入口=我的 Tab「关于与反馈」行；纯静态零 HTTP） */
  | { readonly kind: 'me-about' }
  /**
   * 意见反馈表单页（14.35b；入口=关于页「意见反馈」行；进页零 HTTP）。
   * target 存在=举报预填形态（14.35d，路径段 /me/feedback/<group|private>/<id>；
   * 入口=群设置页「举报该群」/ 好友详情页「举报用户」），缺省=意见反馈形态
   * （21ag：不再提供手选投诉群/投诉用户）。
   */
  | { readonly kind: 'me-feedback'; readonly target?: FeedbackTarget }
  /** 反馈历史页（14.35c；入口=关于页「我的反馈」行；list_feedback 恰 1/页不缓存） */
  | { readonly kind: 'me-feedback-history' }
  /** 归档会话页（14.36b；入口=会话列表顶部「归档会话」入口行；纯本地分流零 HTTP） */
  | { readonly kind: 'archive' }
  /**
   * 安装引导页（16b；/install）。**唯一不需要登录的非登录页**：iOS 用户要先在
   * Safari 里装描述文件才能用，守卫对它一律放行（nav/guard.ts）。
   */
  | { readonly kind: 'install' }
  /** 群二维码页（14.32；入口=群设置页「群二维码」行；深链垫群设置页） */
  | { readonly kind: 'group-qrcode'; readonly groupId: number }
  /** 创建（packId=0）/ 修改重审（packId>0，仅待审/被拒）表情包 */
  | { readonly kind: 'sticker-pack-edit'; readonly packId: number };

/** 举报预填目标（14.35d；旧版 FeedbackPage.reportGroup/.reportUser 同口径） */
export interface FeedbackTarget {
  readonly scope: 'group' | 'private';
  /** 群 ID（group）/ 对方用户 ID（private），恒 > 0 */
  readonly id: number;
}

export const HOME_CHATS: AppRoute = { kind: 'home', tab: 'chats' };

/** route → 以 `/` 开头的 pathname（不含 search/hash）。 */
export function routePath(route: AppRoute): string {
  switch (route.kind) {
    case 'home':
      return route.tab === 'chats' ? '/' : `/${route.tab}`;
    case 'chat': {
      // conversationKey 构造处已保证形如 dm:<数字>/group:<数字>
      const [scope, id] = route.conversationKey.split(':');
      return `/chat/${scope}/${id}`;
    }
    case 'login':
      return '/login';
    case 'set-password':
      return '/set-password';
    case 'dev-vlist':
      return '/dev/vlist';
    case 'sticker-packs':
      return '/stickers';
    case 'contact-search':
      return '/contacts/search';
    case 'contact-applies':
      return `/contacts/applies/${route.applyKind}`;
    case 'friend-detail':
      return `/contacts/friend/${route.friendId}`;
    case 'friend-groups':
      return '/contacts/groups';
    case 'blacklist':
      return '/contacts/blacklist';
    case 'announcements':
      return '/contacts/announcements';
    case 'create-group':
      return '/contacts/create-group';
    case 'group-settings':
      return `/chat/group/${route.groupId}/settings`;
    case 'group-manage':
      return `/chat/group/${route.groupId}/settings/manage`;
    case 'group-members':
      return `/chat/group/${route.groupId}/settings/members`;
    case 'group-invite':
      return `/chat/group/${route.groupId}/settings/invite`;
    case 'group-log':
      return `/chat/group/${route.groupId}/settings/log`;
    case 'group-notice':
      return `/chat/group/${route.groupId}/settings/notice`;
    case 'me-profile':
      return '/me/profile';
    case 'me-privacy':
      return '/me/privacy';
    case 'me-appearance':
      return '/me/appearance';
    case 'me-qrcode':
      return '/me/qrcode';
    case 'me-accounts':
      return '/me/accounts';
    case 'add-account':
      return '/me/accounts/add';
    case 'me-devices':
      return '/me/accounts/devices';
    case 'me-2fa':
      return '/me/accounts/2fa';
    case 'me-route':
      return '/me/route';
    case 'me-about':
      return '/me/about';
    case 'me-feedback':
      return route.target
        ? `/me/feedback/${route.target.scope}/${route.target.id}`
        : '/me/feedback';
    case 'me-feedback-history':
      return '/me/feedback/history';
    case 'archive':
      return '/archive';
    case 'install':
      return '/install';
    case 'group-qrcode':
      return `/chat/group/${route.groupId}/settings/qrcode`;
    case 'sticker-pack-edit':
      return route.packId > 0 ? `/stickers/edit/${route.packId}` : '/stickers/new';
    default:
      return unreachable(route);
  }
}

/** pathname → route；不认识的路径返回 undefined（调用方自行兜底到 HOME_CHATS）。 */
export function parseRoute(pathname: string): AppRoute | undefined {
  // 去掉末尾斜杠（根路径除外），容忍 `/contacts/` 这类手输形态
  const normalized = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  const segments = normalized.split('/').filter((s) => s.length > 0);

  if (segments.length === 0) return HOME_CHATS;
  if (segments.length === 1) {
    switch (segments[0]) {
      case 'contacts':
        return { kind: 'home', tab: 'contacts' };
      case 'me':
        return { kind: 'home', tab: 'me' };
      case 'login':
        return { kind: 'login' };
      case 'set-password':
        return { kind: 'set-password' };
      case 'stickers':
        return { kind: 'sticker-packs' };
      case 'archive':
        return { kind: 'archive' };
      case 'install':
        return { kind: 'install' };
      default:
        return undefined;
    }
  }
  if (segments.length === 2 && segments[0] === 'dev' && segments[1] === 'vlist') {
    return { kind: 'dev-vlist' };
  }
  if (segments.length === 2 && segments[0] === 'me' && segments[1] === 'profile') {
    return { kind: 'me-profile' };
  }
  if (segments.length === 2 && segments[0] === 'me' && segments[1] === 'privacy') {
    return { kind: 'me-privacy' };
  }
  if (segments.length === 2 && segments[0] === 'me' && segments[1] === 'appearance') {
    return { kind: 'me-appearance' };
  }
  if (segments.length === 2 && segments[0] === 'me' && segments[1] === 'qrcode') {
    return { kind: 'me-qrcode' };
  }
  if (segments.length === 2 && segments[0] === 'me' && segments[1] === 'accounts') {
    return { kind: 'me-accounts' };
  }
  if (segments.length === 2 && segments[0] === 'me' && segments[1] === 'about') {
    return { kind: 'me-about' };
  }
  if (segments.length === 2 && segments[0] === 'me' && segments[1] === 'route') {
    return { kind: 'me-route' };
  }
  if (segments.length === 2 && segments[0] === 'me' && segments[1] === 'feedback') {
    return { kind: 'me-feedback' };
  }
  if (
    segments.length === 3 &&
    segments[0] === 'me' &&
    segments[1] === 'feedback' &&
    segments[2] === 'history'
  ) {
    return { kind: 'me-feedback-history' };
  }
  if (
    segments.length === 4 &&
    segments[0] === 'me' &&
    segments[1] === 'feedback' &&
    (segments[2] === 'group' || segments[2] === 'private') &&
    /^\d+$/.test(segments[3] ?? '') &&
    Number(segments[3]) > 0
  ) {
    return { kind: 'me-feedback', target: { scope: segments[2], id: Number(segments[3]) } };
  }
  if (
    segments.length === 3 &&
    segments[0] === 'me' &&
    segments[1] === 'accounts' &&
    segments[2] === 'add'
  ) {
    return { kind: 'add-account' };
  }
  if (segments.length === 3 && segments[0] === 'me' && segments[1] === 'accounts') {
    if (segments[2] === 'devices') return { kind: 'me-devices' };
    if (segments[2] === '2fa') return { kind: 'me-2fa' };
  }
  if (segments.length === 2 && segments[0] === 'contacts' && segments[1] === 'search') {
    return { kind: 'contact-search' };
  }
  if (segments.length === 2 && segments[0] === 'contacts' && segments[1] === 'groups') {
    return { kind: 'friend-groups' };
  }
  if (segments.length === 2 && segments[0] === 'contacts' && segments[1] === 'blacklist') {
    return { kind: 'blacklist' };
  }
  if (segments.length === 2 && segments[0] === 'contacts' && segments[1] === 'announcements') {
    return { kind: 'announcements' };
  }
  if (segments.length === 2 && segments[0] === 'contacts' && segments[1] === 'create-group') {
    return { kind: 'create-group' };
  }
  if (
    segments.length === 3 &&
    segments[0] === 'contacts' &&
    segments[1] === 'applies' &&
    (segments[2] === 'friend' || segments[2] === 'group')
  ) {
    return { kind: 'contact-applies', applyKind: segments[2] };
  }
  if (
    segments.length === 3 &&
    segments[0] === 'contacts' &&
    segments[1] === 'friend' &&
    /^\d+$/.test(segments[2] ?? '')
  ) {
    return { kind: 'friend-detail', friendId: Number(segments[2]) };
  }
  if (segments.length === 2 && segments[0] === 'stickers' && segments[1] === 'new') {
    return { kind: 'sticker-pack-edit', packId: 0 };
  }
  if (
    segments.length === 3 &&
    segments[0] === 'stickers' &&
    segments[1] === 'edit' &&
    /^\d+$/.test(segments[2] ?? '')
  ) {
    return { kind: 'sticker-pack-edit', packId: Number(segments[2]) };
  }
  if (
    segments.length === 3 &&
    segments[0] === 'chat' &&
    (segments[1] === 'dm' || segments[1] === 'group') &&
    /^\d+$/.test(segments[2] ?? '')
  ) {
    return { kind: 'chat', conversationKey: `${segments[1]}:${segments[2]}` };
  }
  if (
    segments.length === 4 &&
    segments[0] === 'chat' &&
    segments[1] === 'group' &&
    /^\d+$/.test(segments[2] ?? '') &&
    segments[3] === 'settings'
  ) {
    return { kind: 'group-settings', groupId: Number(segments[2]) };
  }
  if (
    segments.length === 5 &&
    segments[0] === 'chat' &&
    segments[1] === 'group' &&
    /^\d+$/.test(segments[2] ?? '') &&
    segments[3] === 'settings'
  ) {
    const groupId = Number(segments[2]);
    switch (segments[4]) {
      case 'manage':
        return { kind: 'group-manage', groupId };
      case 'members':
        return { kind: 'group-members', groupId };
      case 'invite':
        return { kind: 'group-invite', groupId };
      case 'log':
        return { kind: 'group-log', groupId };
      case 'notice':
        return { kind: 'group-notice', groupId };
      case 'qrcode':
        return { kind: 'group-qrcode', groupId };
      default:
        return undefined;
    }
  }
  return undefined;
}

export function isSameRoute(a: AppRoute, b: AppRoute): boolean {
  if (a.kind !== b.kind) return false;
  switch (a.kind) {
    case 'home':
      return a.tab === (b as Extract<AppRoute, { kind: 'home' }>).tab;
    case 'chat':
      return a.conversationKey === (b as Extract<AppRoute, { kind: 'chat' }>).conversationKey;
    case 'sticker-pack-edit':
      return a.packId === (b as Extract<AppRoute, { kind: 'sticker-pack-edit' }>).packId;
    case 'contact-applies':
      return a.applyKind === (b as Extract<AppRoute, { kind: 'contact-applies' }>).applyKind;
    case 'friend-detail':
      return a.friendId === (b as Extract<AppRoute, { kind: 'friend-detail' }>).friendId;
    case 'group-settings':
      return a.groupId === (b as Extract<AppRoute, { kind: 'group-settings' }>).groupId;
    case 'group-manage':
      return a.groupId === (b as Extract<AppRoute, { kind: 'group-manage' }>).groupId;
    case 'group-members':
      return a.groupId === (b as Extract<AppRoute, { kind: 'group-members' }>).groupId;
    case 'group-invite':
      return a.groupId === (b as Extract<AppRoute, { kind: 'group-invite' }>).groupId;
    case 'group-log':
      return a.groupId === (b as Extract<AppRoute, { kind: 'group-log' }>).groupId;
    case 'group-notice':
      return a.groupId === (b as Extract<AppRoute, { kind: 'group-notice' }>).groupId;
    case 'group-qrcode':
      return a.groupId === (b as Extract<AppRoute, { kind: 'group-qrcode' }>).groupId;
    case 'login':
    case 'set-password':
    case 'dev-vlist':
    case 'sticker-packs':
    case 'contact-search':
    case 'friend-groups':
    case 'blacklist':
    case 'announcements':
    case 'create-group':
    case 'me-profile':
    case 'me-privacy':
    case 'me-appearance':
    case 'me-qrcode':
    case 'me-accounts':
    case 'add-account':
    case 'me-devices':
    case 'me-2fa':
    case 'me-about':
    case 'me-route':
    case 'me-feedback-history':
    case 'archive':
    case 'install':
      return true;
    case 'me-feedback': {
      // 预填形态与手选形态、不同目标互为不同页（14.35d）
      const t = (b as Extract<AppRoute, { kind: 'me-feedback' }>).target;
      return a.target?.scope === t?.scope && a.target?.id === t?.id;
    }
    default:
      return unreachable(a);
  }
}

/**
 * 按 URL 合成初始页面栈（刷新/深链进入时用）：
 * 深层页面在栈上垫一个根页面，让返回键逐级出栈而不是直接退出站点。
 */
export function stackForPath(pathname: string): AppRoute[] {
  const route = parseRoute(pathname) ?? HOME_CHATS;
  switch (route.kind) {
    case 'chat':
      return [HOME_CHATS, route];
    case 'sticker-packs':
      return [HOME_CHATS, route];
    // 深链/刷新进归档页：返回键回会话列表（入口行就在那）
    case 'archive':
      return [HOME_CHATS, route];
    // 深链/刷新进搜索页/申请页/好友详情页/分组管理页/黑名单页/公告页/建群页：返回键回通讯录 Tab
    case 'contact-search':
    case 'contact-applies':
    case 'friend-detail':
    case 'friend-groups':
    case 'blacklist':
    case 'announcements':
    case 'create-group':
      return [{ kind: 'home', tab: 'contacts' }, route];
    // 深链/刷新进编辑页：返回键先回我的表情包列表再回首页
    case 'sticker-pack-edit':
      return [HOME_CHATS, { kind: 'sticker-packs' }, route];
    // 深链/刷新进编辑资料页/隐私设置页/外观设置页/我的二维码页：返回键回我的 Tab
    case 'me-profile':
    case 'me-privacy':
    case 'me-appearance':
    case 'me-qrcode':
    case 'me-accounts':
    case 'me-about':
    case 'me-route':
      return [{ kind: 'home', tab: 'me' }, route];
    // 深链/刷新进添加账号页/登录设备页/两步验证页：返回键先回账号管理页再回我的 Tab
    case 'add-account':
    case 'me-devices':
    case 'me-2fa':
      return [{ kind: 'home', tab: 'me' }, { kind: 'me-accounts' }, route];
    // 深链/刷新进反馈历史页：返回键先回关于页再回我的 Tab（入口在关于页）
    case 'me-feedback-history':
      return [{ kind: 'home', tab: 'me' }, { kind: 'me-about' }, route];
    // 反馈/举报页按入口垫栈（14.35d）：意见反馈=关于页链；举报预填形态=各自入口链
    // （群=群聊页→群设置页，用户=通讯录 Tab→好友详情页），返回键逐级回举报对象
    case 'me-feedback': {
      const target = route.target;
      if (target === undefined) {
        return [{ kind: 'home', tab: 'me' }, { kind: 'me-about' }, route];
      }
      return target.scope === 'group'
        ? [
            HOME_CHATS,
            { kind: 'chat', conversationKey: `group:${target.id}` },
            { kind: 'group-settings', groupId: target.id },
            route,
          ]
        : [{ kind: 'home', tab: 'contacts' }, { kind: 'friend-detail', friendId: target.id }, route];
    }
    // 深链/刷新进群设置页：返回键先回该群聊天页再回首页
    case 'group-settings':
      return [HOME_CHATS, { kind: 'chat', conversationKey: `group:${route.groupId}` }, route];
    // 深链/刷新进群管理页/群成员页/邀请页/进出记录页/公告页：返回键群设置页→群聊页→首页逐级出栈
    case 'group-manage':
    case 'group-members':
    case 'group-invite':
    case 'group-log':
    case 'group-notice':
    case 'group-qrcode':
      return [
        HOME_CHATS,
        { kind: 'chat', conversationKey: `group:${route.groupId}` },
        { kind: 'group-settings', groupId: route.groupId },
        route,
      ];
    // 安装页是落地页：深链进来**不垫首页**。未登录用户按返回键应退出站点，
    // 而不是落到会被守卫弹走的首页（弹到登录页等于绕回来，返回键就废了）。
    case 'install':
      return [route];
    case 'home':
    case 'login':
    case 'set-password':
    case 'dev-vlist':
      return [route];
    default:
      return unreachable(route);
  }
}
